import { Component } from 'react'
import gql from 'graphql-tag'
import moment from 'moment'
import { Alert, Badge, Button, Modal, ModalBody, ModalFooter, ModalHeader, Progress, Table } from 'reactstrap'

import withApolloClient from '../../withApolloClient'
import toTitleCase from '../../Functions/toTitleCase'
import AttachmentViewer from '../../Drivers/Interactions/AttachmentViewer'
import './Modals.css'
import {
    MERGE_FIELD_GQL,
    ENUM_CHOICE_TYPES,
    diffIncidents,
    defaultResolutions,
    buildFieldOverrides,
    EDITED,
} from './incidentMergeFields'
import IncidentFieldMergeStep from './IncidentFieldMergeStep'

// One incident's transferrable rows. The same selection set is used for both sides, so
// swapping direction is local state rather than another round-trip.
const TRANSFERRABLE_GQL = `
    crmNotesCount
    incidentdocumentSet {
        edges { node { id pk isArchived url documentType { name } } }
    }
    incidentpartySet {
        edges { node { id pk partyName partyType isArchived isDefault } }
    }
    liabilityclaimSet {
        edges { node { id pk externalId isArchived tpa { name } } }
    }
    subrogationclaimSet {
        edges { node { id pk externalId isArchived tpa { name } } }
    }
    taskSet(isAutomated: false, isArchived: false) {
        edges { node { id pk title status commentsCount assignedTo { edges { node { id firstName lastName username } } } } }
    }
`

const ENUM_CHOICES_GQL = ENUM_CHOICE_TYPES.map(({ alias, typeName }) => `
    ${alias}: __type(name: "${typeName}") {
        enumValues { name description }
    }
`).join("")

const IncidentMergeDataQuery = gql`
query IncidentMergeData($fromIncidentId: ID!, $toIncidentId: ID!) {
    fromIncident: incident(incidentId: $fromIncidentId) {
        ${MERGE_FIELD_GQL}
        ${TRANSFERRABLE_GQL}
    }
    toIncident: incident(incidentId: $toIncidentId) {
        ${MERGE_FIELD_GQL}
        ${TRANSFERRABLE_GQL}
    }
    ${ENUM_CHOICES_GQL}
}`

const TransferIncidentDataMutation = gql`
mutation TransferIncidentData($input: TransferIncidentDataMutationInput!) {
    transferIncidentData(input: $input) {
        ok
        transferredCount
        updatedFieldCount
        sourceArchived
        errors { messages }
    }
}`

const EMPTY_SELECTION = { documents: [], parties: [], liabilityClaims: [], subroClaims: [], tasks: [] }

const ArchivedCell = ({ isArchived }) =>
    isArchived
        ? <Badge color="secondary" pill>Archived</Badge>
        : <span className="text-muted">Active</span>

class TransferIncidentDataModal extends Component {
    constructor(props) {
        super(props)
        // Default the lower incident id to the destination so the oldest record is
        // the one that survives. The swap button still reverses it.
        const [to, from] = [props.fromIncident, props.toIncident].sort(
            (a, b) => (a?.pk ?? Infinity) - (b?.pk ?? Infinity)
        )
        this.state = {
            from,
            to,
            incidents: null,
            choiceMaps: {},
            loadingData: false,
            submitting: false,
            error: null,
            transferredCount: null,
            selected: { ...EMPTY_SELECTION },
            moveCrmNotes: false,
            viewingDocument: null,
            step: 1,
            resolutions: {},
            editedValues: {},
            updatedFieldCount: null,
            // Merges are nearly always consolidating a duplicate away, so default to on.
            archiveSource: true,
            sourceArchived: null,
        }
    }

    componentDidMount() {
        this.loadData()
    }

    renderIncidentCard = (label, incident) => {
        const date = incident?.incidentDate ? moment(incident.incidentDate).format("MM/DD/YYYY") : "No date"
        // The direction can be swapped, so flag whichever card is the incident whose
        // detail page is open behind the modal rather than assuming it is the target.
        const isOpenBehind = !!incident?.id && incident.id === this.props.toIncident?.id
        return (
            <div style={{ flex: 1, border: "1px solid #e5e7eb", borderRadius: 8, padding: "12px 16px", background: "#fff", minWidth: 0 }}>
                <div className="merge-card-heading mb-1">
                    <span className="merge-card-label text-muted text-uppercase">{label}</span>
                    {isOpenBehind && (
                        <Badge color="dark" pill className="merge-card-open-badge">
                            Currently open
                        </Badge>
                    )}
                </div>
                <div style={{ fontWeight: 600, wordBreak: "break-word" }}>{incident?.title || "Untitled incident"}</div>
                <div className="text-muted mt-1" style={{ fontSize: 12 }}>ID #{incident?.pk || "--"} · {date}</div>
            </div>
        )
    }

    // Both incidents are fetched once; swapping direction only flips which one is the source.
    loadData = () => {
        const { fromIncident, toIncident } = this.props
        if (!fromIncident?.id || !toIncident?.id) return
        this.setState({ loadingData: true, error: null })
        this.props.apolloClient.query({
            query: IncidentMergeDataQuery,
            variables: { fromIncidentId: fromIncident.id, toIncidentId: toIncident.id },
            fetchPolicy: "network-only",
        }).then((result) => {
            const choiceMaps = ENUM_CHOICE_TYPES.reduce(
                (acc, { alias }) => ({ ...acc, [alias]: result?.data?.[alias] }),
                {}
            )
            this.setState({
                incidents: {
                    [fromIncident.id]: result?.data?.fromIncident || null,
                    [toIncident.id]: result?.data?.toIncident || null,
                },
                choiceMaps,
                loadingData: false,
                selected: { ...EMPTY_SELECTION },
                moveCrmNotes: false,
            })
        }).catch((error) => {
            this.setState({ loadingData: false, error: error.message || "Could not load incident data." })
        })
    }

    // The transferrable rows belonging to whichever incident is currently the source.
    dataFor = (incident) => {
        const node = this.state.incidents?.[incident?.id]
        if (!node) return null
        return {
            documents: node.incidentdocumentSet?.edges?.map(({ node: item }) => item) || [],
            // The default party belongs to the incident itself and cannot be moved, so exclude it.
            parties: node.incidentpartySet?.edges?.map(({ node: item }) => item).filter((item) => !item.isDefault) || [],
            liabilityClaims: node.liabilityclaimSet?.edges?.map(({ node: item }) => item) || [],
            subroClaims: node.subrogationclaimSet?.edges?.map(({ node: item }) => item) || [],
            tasks: node.taskSet?.edges?.map(({ node: item }) => item) || [],
            crmNotesCount: node.crmNotesCount || 0,
        }
    }

    swap = () => {
        this.setState((prev) => ({
            from: prev.to,
            to: prev.from,
            selected: { ...EMPTY_SELECTION },
            moveCrmNotes: false,
            resolutions: {},
            editedValues: {},
            step: 1,
            error: null,
        }))
    }

    resolveField = (key, side) => {
        this.setState((prev) => ({ resolutions: { ...prev.resolutions, [key]: side }, error: null }))
    }

    editField = (key, value) => {
        this.setState((prev) => ({
            resolutions: { ...prev.resolutions, [key]: EDITED },
            editedValues: { ...prev.editedValues, [key]: value },
            error: null,
        }))
    }

    toggleSelect = (type, id) => {
        this.setState((prev) => {
            const current = prev.selected[type]
            const next = current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
            return { selected: { ...prev.selected, [type]: next }, error: null }
        })
    }

    toggleSelectAll = (rows, select) => {
        this.setState((prev) => {
            const selected = {
                documents: [...prev.selected.documents],
                parties: [...prev.selected.parties],
                liabilityClaims: [...prev.selected.liabilityClaims],
                subroClaims: [...prev.selected.subroClaims],
                tasks: [...prev.selected.tasks],
            }
            rows.forEach((row) => {
                const set = new Set(selected[row.__type])
                select ? set.add(row.id) : set.delete(row.id)
                selected[row.__type] = Array.from(set)
            })
            return { selected, error: null }
        })
    }

    handleClose = () => {
        const transferSucceeded = this.state.transferredCount !== null
        this.setState({
            selected: { ...EMPTY_SELECTION },
            moveCrmNotes: false,
            error: null,
            submitting: false,
            transferredCount: null,
            updatedFieldCount: null,
            sourceArchived: null,
            archiveSource: true,
            step: 1,
            resolutions: {},
            editedValues: {},
        })
        if (transferSucceeded && this.props.refetchQuery) this.props.refetchQuery()
        this.props.handleClose()
    }

    submit = () => {
        const { from, to, selected, moveCrmNotes, resolutions, editedValues, archiveSource } = this.state
        const input = {
            fromIncidentId: from.id,
            toIncidentId: to.id,
            documentIds: selected.documents,
            partyIds: selected.parties,
            liabilityClaimIds: selected.liabilityClaims,
            subrogationClaimIds: selected.subroClaims,
            taskIds: selected.tasks,
            moveCrmNotes,
            archiveSource,
            fieldOverrides: buildFieldOverrides(resolutions, editedValues),
        }
        this.setState({ submitting: true, error: null })
        this.props.apolloClient.mutate({
            mutation: TransferIncidentDataMutation,
            variables: { input },
        }).then((result) => {
            const response = result?.data?.transferIncidentData
            if (response?.ok) {
                this.setState({
                    transferredCount: response.transferredCount ?? 0,
                    updatedFieldCount: response.updatedFieldCount ?? 0,
                    sourceArchived: !!response.sourceArchived,
                    submitting: false,
                })
                return
            }
            const error = response?.errors?.[0]?.messages?.toString() || "Could not transfer incident data."
            this.setState({ error, submitting: false })
        }).catch((error) => {
            this.setState({ error: error.message || "An error has occurred.", submitting: false })
        })
    }

    renderTable = (title, rows, columns, note = null) => {
        if (!rows.length) {
            return (
                <div className="mb-4">
                    <h6 className="text-uppercase text-muted mb-2" style={{ fontSize: 12, letterSpacing: 0.5 }}>{title}</h6>
                    <p className="text-muted small mb-0">Nothing to transfer.</p>
                </div>
            )
        }
        const allSelected = rows.every((row) => this.state.selected[row.__type].includes(row.id))
        return (
            <div className="mb-4">
                <h6 className="text-uppercase text-muted mb-2" style={{ fontSize: 12, letterSpacing: 0.5 }}>
                    {title} <Badge color="light" className="text-dark border ml-1">{rows.length}</Badge>
                </h6>
                {note && (
                    <p className="small mb-2" style={{ color: "#8a6d3b" }}>
                        <i className="fa fa-info-circle mr-1" aria-hidden="true" />
                        {note}
                    </p>
                )}
                <Table size="sm" hover className="mb-0 align-middle">
                    <thead>
                        <tr className="text-muted" style={{ fontSize: 12 }}>
                            <th style={{ width: 36 }}>
                                <input type="checkbox" checked={allSelected} onChange={() => this.toggleSelectAll(rows, !allSelected)} />
                            </th>
                            {columns.map((col) => <th key={col.header} style={col.width ? { width: col.width } : null}>{col.header}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => {
                            const checked = this.state.selected[row.__type].includes(row.id)
                            return (
                                <tr key={`${row.__type}-${row.id}`} className={checked ? "table-active" : ""}>
                                    <td>
                                        <input type="checkbox" checked={checked} onChange={() => this.toggleSelect(row.__type, row.id)} />
                                    </td>
                                    {columns.map((col) => <td key={col.header}>{col.render(row)}</td>)}
                                </tr>
                            )
                        })}
                    </tbody>
                </Table>
            </div>
        )
    }

    render() {
        const { from, to, loadingData, submitting, error, moveCrmNotes, transferredCount, archiveSource } = this.state
        const data = this.dataFor(from)
        const { step, resolutions, editedValues, choiceMaps, incidents, updatedFieldCount, sourceArchived } = this.state
        const fromNode = incidents?.[from?.id]
        const toNode = incidents?.[to?.id]
        const { differing, identical } = fromNode && toNode
            ? diffIncidents(fromNode, toNode, choiceMaps)
            : { differing: [], identical: [] }
        const activeResolutions = { ...defaultResolutions(differing), ...resolutions }
        const transferSucceeded = transferredCount !== null

        const documentRows = (data?.documents || []).map((doc) => ({
            __type: "documents", id: doc.id, pk: doc.pk, documentType: doc.documentType?.name, isArchived: doc.isArchived, url: doc.url,
        }))
        const partyRows = (data?.parties || []).map((party) => ({
            __type: "parties", id: party.id, pk: party.pk, partyName: party.partyName, partyType: party.partyType, isArchived: party.isArchived,
        }))
        const claimRows = [
            ...(data?.liabilityClaims || []).map((claim) => ({ __type: "liabilityClaims", typeLabel: "Liability", id: claim.id, pk: claim.pk, externalId: claim.externalId, tpa: claim.tpa?.name, isArchived: claim.isArchived })),
            ...(data?.subroClaims || []).map((claim) => ({ __type: "subroClaims", typeLabel: "Subrogation", id: claim.id, pk: claim.pk, externalId: claim.externalId, tpa: claim.tpa?.name, isArchived: claim.isArchived })),
        ]
        const taskRows = (data?.tasks || []).map((task) => ({
            __type: "tasks",
            id: task.id,
            title: task.title,
            commentsCount: task.commentsCount || 0,
            status: task.status,
            assignedTo: (task.assignedTo?.edges || [])
                .map(({ node }) => `${node.firstName || ""} ${node.lastName || ""}`.trim() || node.username)
                .filter(Boolean)
                .join(", "),
        }))

        const { viewingDocument } = this.state

        return (
            <>
            <Modal isOpen={this.props.open} toggle={this.handleClose} size="lg" scrollable>
                <ModalHeader
                    close={
                        <button type="button" className="close" aria-label="Close" onClick={this.handleClose}>
                            <i className="fa fa-times" aria-hidden="true" />
                        </button>
                    }
                >
                    Merge Incident Data
                </ModalHeader>
                <ModalBody>
                    {error && <Alert color="danger">{error}</Alert>}
                    {!transferSucceeded && (
                        <div className="merge-step-indicator">
                            <span className={step === 1 ? "merge-step-current" : ""}>1. Field values</span>
                            <i className="fa fa-angle-right" aria-hidden="true" />
                            <span className={step === 2 ? "merge-step-current" : ""}>2. Records</span>
                        </div>
                    )}
                    {transferSucceeded ? (
                        <Alert color="success" className="mb-0">
                            {(updatedFieldCount > 0
                                ? `Updated ${updatedFieldCount} ${updatedFieldCount === 1 ? "field" : "fields"} and transferred `
                                : "Transferred ") +
                                `${transferredCount} ${transferredCount === 1 ? "item" : "items"} from incident #${from?.pk || "--"} to incident #${to?.pk || "--"}.` +
                                (sourceArchived ? ` Incident #${from?.pk || "--"} was archived as a duplicate.` : "")}
                        </Alert>
                    ) : <>
                    <div className="d-flex align-items-center mb-4" style={{ gap: 12 }}>
                        {this.renderIncidentCard("Moving from", from)}
                        <Button color="primary" outline onClick={this.swap} disabled={loadingData || submitting} title="Swap direction">
                            <i className="fa fa-exchange" aria-hidden="true" />
                        </Button>
                        {this.renderIncidentCard("Moving to", to)}
                    </div>

                    {loadingData ? (
                        <Progress animated color="info" value="100" />
                    ) : step === 1 ? (
                        <IncidentFieldMergeStep
                            from={fromNode}
                            to={toNode}
                            differing={differing}
                            identical={identical}
                            resolutions={activeResolutions}
                            onResolve={this.resolveField}
                            editedValues={editedValues}
                            onEdit={this.editField}
                        />
                    ) : data && (
                        <>
                            <Alert color="info" className="py-2">
                                <i className="fa fa-info-circle mr-1" aria-hidden="true" />
                                The data below belongs to <strong>{from?.title || "Untitled incident"} (ID #{from?.pk || "--"})</strong> — the incident you're moving data <strong>from</strong>. Selected items will be moved into <strong>{to?.title || "Untitled incident"} (ID #{to?.pk || "--"})</strong>. Use the swap button above to reverse the direction. Any damages and the associated damage transaction will also be transferred automatically as part of this action, when applicable.
                            </Alert>
                            {this.renderTable("Documents", documentRows, [
                                { header: "ID", width: 70, render: (row) => row.pk },
                                { header: "Document Type", render: (row) => row.documentType || <span className="text-muted">—</span> },
                                { header: "Status", width: 110, render: (row) => <ArchivedCell isArchived={row.isArchived} /> },
                                {
                                    header: "", width: 90, render: (row) => row.url
                                        ? <Button size="sm" color="link" className="p-0" onClick={() => this.setState({ viewingDocument: { url: row.url, document: (row.url || "").split("?")[0], documentType: row.documentType || "Document" } })}>View</Button>
                                        : <span className="text-muted">—</span>,
                                },
                            ], "Transferred documents are attached directly to the destination incident. Documents filed under a party or claim are moved out and re-filed under the incident itself.")}

                            {this.renderTable("Parties", partyRows, [
                                { header: "ID", width: 70, render: (row) => row.pk },
                                { header: "Name", render: (row) => row.partyName || <span className="text-muted">Unnamed</span> },
                                { header: "Type", render: (row) => row.partyType ? toTitleCase(row.partyType) : <span className="text-muted">—</span> },
                                { header: "Status", width: 110, render: (row) => <ArchivedCell isArchived={row.isArchived} /> },
                            ])}

                            {this.renderTable("Claims", claimRows, [
                                { header: "ID", width: 70, render: (row) => row.pk },
                                { header: "Type", width: 120, render: (row) => <Badge color={row.typeLabel === "Liability" ? "warning" : "info"}>{row.typeLabel}</Badge> },
                                { header: "External ID", render: (row) => row.externalId ? `#${row.externalId}` : <span className="text-muted">—</span> },
                                { header: "TPA", render: (row) => row.tpa || <span className="text-muted">—</span> },
                                { header: "Status", width: 110, render: (row) => <ArchivedCell isArchived={row.isArchived} /> },
                            ])}

                            {this.renderTable("Manual Tasks", taskRows, [
                                { header: "Task", render: (row) => row.title || <span className="text-muted">Untitled</span> },
                                { header: "Updates", width: 90, render: (row) => row.commentsCount },
                                { header: "Status", width: 130, render: (row) => row.status ? toTitleCase(row.status) : <span className="text-muted">—</span> },
                                { header: "Assigned To", render: (row) => row.assignedTo || <span className="text-muted">Unassigned</span> },
                            ])}

                            <div>
                                <h6 className="text-uppercase text-muted mb-2" style={{ fontSize: 12, letterSpacing: 0.5 }}>CRM Notes</h6>
                                {data.crmNotesCount > 0 ? (
                                    <label className="d-flex align-items-center mb-0" style={{ cursor: "pointer" }}>
                                        <input
                                            type="checkbox"
                                            className="mr-2"
                                            checked={moveCrmNotes}
                                            onChange={() => this.setState((prev) => ({ moveCrmNotes: !prev.moveCrmNotes, error: null }))}
                                        />
                                        Move all CRM notes <Badge color="light" className="text-dark border ml-2">{data.crmNotesCount}</Badge>
                                    </label>
                                ) : (
                                    <p className="text-muted small mb-0">Nothing to transfer.</p>
                                )}
                            </div>

                            <div className="mt-4">
                                <h6 className="text-uppercase text-muted mb-2" style={{ fontSize: 12, letterSpacing: 0.5 }}>After Merging</h6>
                                <label className="d-flex align-items-center mb-0" style={{ cursor: "pointer" }}>
                                    <input
                                        type="checkbox"
                                        className="mr-2"
                                        checked={archiveSource}
                                        onChange={() => this.setState((prev) => ({ archiveSource: !prev.archiveSource, error: null }))}
                                    />
                                    Archive incident {from?.pk || "--"} as a duplicate of incident {to?.pk || "--"}
                                </label>
                                <p className="text-muted small mb-0 mt-1">
                                    The archived incident records which incident it was merged into, so the
                                    link stays recoverable. It cannot be archived while it still has an
                                    active liability or subrogation claim.
                                </p>
                            </div>
                        </>
                    )}
                    </>}
                </ModalBody>
                <ModalFooter>
                    {transferSucceeded ? (
                        <Button color="primary" onClick={this.handleClose}>Close</Button>
                    ) : step === 1 ? <>
                        <Button outline color="secondary" onClick={this.handleClose}>Cancel</Button>
                        <Button
                            color="primary"
                            onClick={() => this.setState({ step: 2, error: null })}
                            disabled={loadingData || !fromNode || !toNode}
                        >
                            Next
                        </Button>
                    </> : <>
                        <Button outline color="secondary" onClick={() => this.setState({ step: 1, error: null })} disabled={submitting}>
                            Back
                        </Button>
                        <Button outline color="secondary" onClick={this.handleClose} disabled={submitting}>Cancel</Button>
                        <Button color="primary" onClick={this.submit} disabled={submitting || loadingData || !data}>
                            {submitting ? "Transferring..." : "Transfer"}
                        </Button>
                    </>}
                </ModalFooter>
            </Modal>
            {viewingDocument && (
                <AttachmentViewer
                    open
                    attachment={viewingDocument}
                    handleClose={() => this.setState({ viewingDocument: null })}
                />
            )}
            </>
        )
    }
}

export default withApolloClient(TransferIncidentDataModal)
